/**
 * Step 2: Upload File
 *
 * User selects an .xlsx or .csv file. The smart parser auto-detects
 * the header row, merged cells, and context rows. User can adjust the
 * detected header row and sheet selection.
 */

import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { ParseResult } from '../utils/parseWorksheet';

interface StepUploadFileProps {
	file: File | null;
	parseResult: ParseResult | null;
	isLoading: boolean;
	error: string | null;
	onFileSelect: (file: File) => Promise<void>;
	onSheetChange: (sheetName: string) => Promise<void>;
	onHeaderRowChange: (row: number) => void;
}

export const StepUploadFile: React.FC<StepUploadFileProps> = ({
	file,
	parseResult,
	isLoading,
	error,
	onFileSelect,
	onSheetChange,
	onHeaderRowChange,
}) => {
	const { t } = useTranslation();

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			const dropped = e.dataTransfer.files[0];
			if (dropped) onFileSelect(dropped);
		},
		[onFileSelect]
	);

	const handleFileInput = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const selected = e.target.files?.[0];
			if (selected) onFileSelect(selected);
		},
		[onFileSelect]
	);

	return (
		<Box>
			{/* Drop zone */}
			<Box
				onDragOver={(e) => e.preventDefault()}
				onDrop={handleDrop}
				sx={{
					border: '2px dashed',
					borderColor: file ? 'success.main' : 'divider',
					borderRadius: 2,
					p: 4,
					textAlign: 'center',
					cursor: 'pointer',
					transition: 'border-color 0.2s',
					'&:hover': { borderColor: 'primary.main' },
					mb: 3,
				}}>
				{isLoading ? (
					<CircularProgress />
				) : file ? (
					<Box>
						<CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
						<Typography variant="subtitle1">{file.name}</Typography>
						<Typography variant="caption" color="text.secondary">
							{(file.size / 1024).toFixed(1)} KB
						</Typography>
					</Box>
				) : (
					<Box>
						<CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
						<Typography variant="subtitle1">{t('bulkUpload.upload.dragDrop')}</Typography>
						<Typography variant="caption" color="text.secondary">
							{t('bulkUpload.upload.supportedFormats')}
						</Typography>
					</Box>
				)}

				<Box sx={{ mt: 2 }}>
					<Button variant="outlined" component="label" size="small">
						{file ? t('bulkUpload.upload.chooseDifferentFile') : t('bulkUpload.upload.browseFiles')}
						<input type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFileInput} />
					</Button>
				</Box>
			</Box>

			{/* Error */}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{/* Parse results */}
			{parseResult && (
				<Box>
					{/* Sheet selector (for multi-sheet Excel files) */}
					{parseResult.sheetNames.length > 1 && (
						<FormControl fullWidth size="small" sx={{ mb: 2 }}>
							<InputLabel>{t('bulkUpload.upload.sheet')}</InputLabel>
							<Select
								value={parseResult.selectedSheet}
								label={t('bulkUpload.upload.sheet')}
								onChange={(e) => onSheetChange(e.target.value)}>
								{parseResult.sheetNames.map((name) => (
									<MenuItem key={name} value={name}>
										{name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}

					{/* Header row adjustment */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
						<TextField
							label="Header Row"
							type="number"
							size="small"
							value={parseResult.headerDetection.headerRow}
							onChange={(e) => onHeaderRowChange(Number(e.target.value))}
							inputProps={{ min: 1, max: 50 }}
							sx={{ width: 120 }}
						/>
						<Typography variant="caption" color="text.secondary">
							{parseResult.headerDetection.confidence >= 0.8
								? t('bulkUpload.upload.autoDetectedHigh')
								: t('bulkUpload.upload.autoDetectedVerify')}
						</Typography>
					</Box>

					{/* Summary chips */}
					<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
						<Chip
							label={t('bulkUpload.upload.columns', { count: parseResult.headers.length })}
							color="primary"
							size="small"
							variant="outlined"
						/>
						<Chip
							label={t('bulkUpload.upload.dataRows', { count: parseResult.rows.length })}
							color="primary"
							size="small"
							variant="outlined"
						/>
						{parseResult.contextRows.length > 0 && (
							<Chip
								label={t('bulkUpload.upload.contextRowsDetected', {
									count: parseResult.contextRows.length,
								})}
								color="info"
								size="small"
								variant="outlined"
							/>
						)}
					</Box>

					{/* Context row defaults */}
					{parseResult.contextDefaults.length > 0 && (
						<Alert severity="info" sx={{ mb: 2 }}>
							<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
								{t('bulkUpload.upload.contextDefaultsDetected')}
							</Typography>
							{parseResult.contextDefaults.map((cd, i) => (
								<Typography key={i} variant="body2">
									{cd.label}: <strong>{cd.rawValue}</strong>
									{cd.relation && (
										<>
											{' '}
											({cd.relation.confidence} match
											{cd.relation.resolvedLabel ? ` → ${cd.relation.resolvedLabel}` : ''})
										</>
									)}
								</Typography>
							))}
						</Alert>
					)}
				</Box>
			)}
		</Box>
	);
};
